/**
 * Brand colour for the ordering surface, as design tokens.
 *
 * The ordering menu is TENANT-branded, unlike admin or management which are
 * OrderUp-branded. That is why it cannot simply adopt the shared `--primary`
 * from `@order-up/common` — that token is a near-black platform colour, and
 * every restaurant needs its own.
 *
 * What existed instead: `#dc2626` (Tailwind `red-600`) is the DEFAULT brand
 * colour in `DEFAULT_THEME`, and 12 of the ordering components hardcoded
 * `bg-red-600` / `text-red-600` as though that default were universal, while 5
 * read the restaurant's real colour through an inline `style`. A restaurant
 * that picks any other colour therefore gets it on its header and category
 * chips and OrderUp red on every button, price, cart and modal —
 * `PhoneAuthModal` managed both inside one dialog.
 *
 * These functions derive the token set from one brand colour. On the `#dc2626`
 * default the derived shades land within a few points per channel of the
 * Tailwind classes they replace (hover `#b92020` vs `red-700` `#b91c1c`; tint
 * `#fdf4f4` vs `red-50` `#fef2f2`). NOT an exact match — Tailwind's scale is
 * not a linear mix toward black — but visually indistinguishable, which is
 * what makes this safe to ship without a visual diff.
 */

/** Parse `#rgb` / `#rrggbb` into 0-255 channels. Null when unparseable. */
export function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
    const value = hex.trim().replace(/^#/, '');
    const full =
        value.length === 3
            ? value
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : value;
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
    return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
    };
}

const toHex = (n: number): string =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

/**
 * Relative luminance per WCAG 2.x. Used to decide whether text ON the brand
 * colour should be white or near-black — a fixed `text-white` is unreadable on
 * a pale brand, which is exactly what a hardcoded `bg-red-600 text-white`
 * assumed for everybody.
 */
export function relativeLuminance(hex: string): number | null {
    const rgb = parseHexColor(hex);
    if (!rgb) return null;
    const channel = (c: number): number => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** Mix `hex` toward white (`amount` > 0) or black (`amount` < 0), -1..1. */
export function mixColor(hex: string, amount: number): string | null {
    const rgb = parseHexColor(hex);
    if (!rgb) return null;
    const target = amount >= 0 ? 255 : 0;
    const t = Math.abs(amount);
    return `#${toHex(rgb.r + (target - rgb.r) * t)}${toHex(rgb.g + (target - rgb.g) * t)}${toHex(
        rgb.b + (target - rgb.b) * t,
    )}`;
}

/** The CSS custom properties every ordering component reads. */
export interface BrandTokens {
    '--menu-primary': string;
    '--menu-primary-hover': string;
    '--menu-primary-foreground': string;
    '--menu-primary-tint': string;
}

/**
 * Derive the token set from a brand colour.
 *
 * `hover` darkens by 12%, which lands `#dc2626` on very nearly `red-700`, the
 * value the old hardcoded pairs used. `tint` is the pale wash the old
 * `bg-red-50` / `bg-red-100` classes provided. `foreground` flips to near-black
 * on a light brand instead of assuming white.
 *
 * An unparseable colour falls back to the default rather than emitting an
 * invalid custom property, because a broken `--menu-primary` would render
 * buttons transparent — worse than the wrong shade.
 */
export function deriveBrandTokens(primary: string, fallback = '#dc2626'): BrandTokens {
    const base = parseHexColor(primary) ? primary : fallback;
    const luminance = relativeLuminance(base) ?? 0;
    return {
        '--menu-primary': base,
        '--menu-primary-hover': mixColor(base, -0.16) ?? base,
        // 0.5 is the midpoint of the WCAG luminance range; above it a white
        // label loses contrast, so switch to the near-black used elsewhere.
        '--menu-primary-foreground': luminance > 0.5 ? '#171717' : '#ffffff',
        '--menu-primary-tint': mixColor(base, 0.95) ?? base,
    };
}
